import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Chip, Input, Switch } from '@heroui/react'
import { explain } from '../lib/staffErrors'
import { timezoneOptions } from '../lib/timezones'
import { showToast } from '../ui/toast'
import { useStaffText } from '../ui/StaffText'
import { useTextLanguages } from '../lib/i18n/useCustomerLanguages'
import { missingLanguages } from '../lib/i18n/missing'
import { MissingMark } from '../ui/MissingMark'
import { SameSwitch } from '../ui/SameSwitch'
import { isSame, ownWords, withWords } from '../lib/i18n/same'
import { SETUP_CHANGED } from './setupChanged'
import { Notice } from './settingsKit'
import { Sheet } from './Sheet'
import '../styles/admin.css'
import { Pick as SetupPick } from './setupKit'

/**
 * The pieces every administration section is built from.
 *
 * One idea runs through them: a list is rows, a row opens a panel, and nothing is
 * saved until the person says so. The panel is ./Sheet — the same side panel as
 * the rest of the staff screens, stackable, so a row inside a panel can open one
 * of its own.
 *
 * Nothing here deletes. Retiring is a switch like any other field, because a
 * retired row is still a row: tickets issued this morning point at it, and so do
 * the figures somebody reads next quarter.
 */

/**
 * Fired with the sentence whenever an administration call is refused, so the
 * panel somebody pressed Save in can say it where they are looking. The section's
 * own notice is behind the panel's scrim.
 */
export const ADMIN_REFUSED = 'qms:admin-refused'

const refused = (message: string) => {
  if (message) window.dispatchEvent(new CustomEvent<string>(ADMIN_REFUSED, { detail: message }))
}

export function Field({
  label,
  hint,
  wide,
  group,
  children,
}: {
  label: string
  hint?: string
  wide?: boolean
  /**
   * Renders a <fieldset>/<legend> instead of a <label> wrapping every child.
   *
   * A <label> implicitly names only the first labelable descendant it wraps. A
   * field whose children are a list of checkboxes, a repeated row of inputs, or
   * "Add a condition" beside the conditions it adds to, is not named correctly by
   * one: the first control quietly inherits the caption, and every other control
   * in the group is named nothing at all.
   *
   * Set this whenever a field wraps more than one control.
   */
  group?: boolean
  children: ReactNode
}) {
  const Tag = group ? 'fieldset' : 'label'
  const Caption = group ? 'legend' : 'span'
  return (
    <Tag className="field" style={wide ? { gridColumn: '1 / -1' } : undefined}>
      <Caption title={hint}>{label}</Caption>
      {children}
    </Tag>
  )
}

export function Text({
  value,
  onChange,
  placeholder,
  uppercase,
  disabled,
  required,
}: {
  value: string
  onChange: (next: string) => void
  placeholder?: string
  uppercase?: boolean
  disabled?: boolean
  required?: boolean
}) {
  return (
    <Input
      fullWidth
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      autoCapitalize={uppercase ? 'characters' : undefined}
      // Upper-cased as it is typed rather than on save, so the field shows what
      // will be stored. A code that changes when the form is submitted looks like
      // the form has misunderstood.
      onChange={(e) => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
    />
  )
}

export function Num({
  value,
  onChange,
  min,
  max,
  disabled,
  label,
}: {
  value: number
  onChange: (next: number) => void
  min?: number
  max?: number
  disabled?: boolean
  /** Its name, when no Field wraps it. */
  label?: string
}) {
  return (
    <Input
      className="admin-num"
      type="number"
      aria-label={label}
      value={value}
      min={min}
      max={max}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
    />
  )
}

/**
 * A name in the three languages a branch in Baku shows.
 *
 * All three are offered and none is required: a language left empty falls back to
 * the others rather than rendering an empty button, which is the behaviour the
 * server's catalog.Name already has. English first, the source language.
 */
export const LANGUAGES: Array<[string, string]> = [['en', 'English'], ['az', 'Azərbaycanca'], ['ru', 'Русский']]

/** A name in the organisation's customer languages; text in other languages stays stored. */
export function Names({
  value,
  onChange,
}: {
  value: Record<string, string>
  onChange: (next: Record<string, string>) => void
}) {
  const { say } = useStaffText()
  const TEXT_LANGUAGES = useTextLanguages()
  const missing = missingLanguages(
    value,
    TEXT_LANGUAGES.map(([c]) => c),
  )
  const def = TEXT_LANGUAGES[0]![0]
  const same = isSame(value)
  // One field with a line per language rather than three fields of their own:
  // it is one name, said three ways.
  if (TEXT_LANGUAGES.length === 1 || same) {
    const field = (
      <label className="field" style={{ gridColumn: '1 / -1' }}>
        <span>
          {say('common.name')} <MissingMark codes={missing} />
        </span>
        <Input fullWidth value={ownWords(value, def)} onChange={(e) => onChange(withWords(value, def, e.target.value, def))} />
      </label>
    )
    if (!same) return field
    return (
      <div className="field" style={{ gridColumn: '1 / -1' }}>
        {field}
        <SameSwitch value={value} onChange={onChange} defaultLang={def} />
      </div>
    )
  }
  return (
    <fieldset className="field admin-names" style={{ gridColumn: '1 / -1' }}>
      <legend>
        {say('common.name')} <MissingMark codes={missing} />
      </legend>
      {TEXT_LANGUAGES.map(([code, label]) => (
        <label key={code} className="admin-names__row">
          <span className="admin-names__lang" title={label}>
            {code}
          </span>
          <Input
            fullWidth
            aria-label={label}
            value={value[code] ?? ''}
            onChange={(e) => onChange({ ...value, [code]: e.target.value })}
          />
        </label>
      ))}
      <SameSwitch value={value} onChange={onChange} defaultLang={def} />
    </fieldset>
  )
}

/** A yes-or-no setting, drawn as a switch. */
export function Check({
  label,
  checked,
  onChange,
  hint,
  disabled,
}: {
  label: string
  checked: boolean
  onChange: (next: boolean) => void
  /** The label's tooltip. */
  hint?: string
  disabled?: boolean
}) {
  return (
    <Switch className="toggle admin-toggle" isSelected={checked} isDisabled={disabled} onChange={onChange}>
      <Switch.Content className="sw-hit">
        <span>
          <strong title={hint}>{label}</strong>
        </span>
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
      </Switch.Content>
    </Switch>
  )
}

/**
 * A row in an administration list, and the panel it opens.
 *
 * The row is the facts worth seeing without opening it; the panel is the form,
 * with Save and Cancel at its foot. Save closes the panel once the server has
 * taken it; a refusal keeps the panel open and says why inside it, because the
 * person's typing is in there and so is their attention.
 *
 * A row with unsaved changes says so. Closing its panel with changes in it asks
 * first, and Cancel puts the saved values back.
 */
export function EditCard({
  title,
  subtitle,
  summary,
  retired,
  dirty,
  busy,
  onSave,
  onCancel,
  startOpen,
  children,
}: {
  title: string
  subtitle?: string
  /** The facts worth seeing without opening the row — a ticket letter, a target. */
  summary?: ReactNode
  retired?: boolean
  dirty: boolean
  busy: boolean
  onSave: () => void
  onCancel?: () => void
  /** A row being added is open: nobody presses "New service" to see a closed one. */
  startOpen?: boolean
  children: ReactNode
}) {
  const { say } = useStaffText()
  const [open, setOpen] = useState(Boolean(startOpen))
  const [error, setError] = useState('')
  const pending = useRef(false)
  const wasBusy = useRef(busy)

  // The refusal of a save pressed here, said in the panel.
  useEffect(() => {
    if (!open) return
    const on = (e: Event) => {
      if (pending.current) setError((e as CustomEvent<string>).detail)
    }
    window.addEventListener(ADMIN_REFUSED, on)
    return () => window.removeEventListener(ADMIN_REFUSED, on)
  }, [open])

  // A save that finished without a refusal closes the panel.
  useEffect(() => {
    if (wasBusy.current && !busy && pending.current) {
      pending.current = false
      if (!error) setOpen(false)
    }
    wasBusy.current = busy
  }, [busy, error])

  const close = () => {
    setOpen(false)
    setError('')
    pending.current = false
    if (dirty || startOpen) onCancel?.()
  }

  return (
    <>
      {!startOpen && (
        <button
          type="button"
          className="admin-row"
          data-retired={retired || undefined}
          onClick={() => {
            setError('')
            setOpen(true)
          }}
        >
          <span className="admin-row__main">
            <span className="admin-row__title">
              {title}
              {retired && (
                <Chip size="sm" variant="soft">
                  {say('common.retiredPill')}
                </Chip>
              )}
              {dirty && (
                <Chip size="sm" variant="soft" color="warning">
                  {say('common.unsaved')}
                </Chip>
              )}
            </span>
            {subtitle && <span className="admin-row__sub">{subtitle}</span>}
          </span>
          {summary && <span className="admin-row__summary">{summary}</span>}
        </button>
      )}
      {open && (
        <Sheet
          title={subtitle && !startOpen ? subtitle : title}
          onClose={close}
          onSubmit={() => {
            setError('')
            pending.current = true
            onSave()
          }}
          submitLabel={say('common.save')}
          valid={dirty}
          busy={busy}
          error={error}
          asForm={false}
        >
          <div className="admin-form">{children}</div>
        </Sheet>
      )}
    </>
  )
}

/**
 * Active rows first, retired ones after them, each group keeping its own order.
 *
 * A retired row is still a row — tickets issued this morning point at it — so it is
 * never hidden. But it is the least useful thing on the screen.
 */
export function activeFirst<T>(rows: T[], isActive: (row: T) => boolean): T[] {
  return [...rows].sort((a, b) => Number(isActive(b)) - Number(isActive(a)))
}

/**
 * act runs one administration call and turns its refusal into a sentence.
 *
 * The server's refusals here are written to be read by the person who met them —
 * "3 people are still waiting in this queue" — so they are shown as they arrive
 * rather than replaced with a generic failure, and announced (ADMIN_REFUSED) so
 * the panel the save came from can say it too.
 *
 * A save that succeeded is announced as well (SETUP_CHANGED), so the version
 * label and lists showing the branch read it again.
 */
export function useAction(onDone: () => Promise<void> | void) {
  const { lang } = useStaffText()
  const [busy, setBusy] = useState(false)
  const [error, setErrorState] = useState('')
  const [notice, setNotice] = useState('')

  // A notice this hook did not just set is a stale one — SETUP_CHANGED fires for
  // every save on every section. run() sets notice only
  // after dispatching and after onDone has resolved, so this handler's
  // setNotice('') always lands before that, never after it.
  useEffect(() => {
    const onChange = () => setNotice('')
    window.addEventListener(SETUP_CHANGED, onChange)
    return () => window.removeEventListener(SETUP_CHANGED, onChange)
  }, [])

  // Stable, like the state setter it wraps: sections list it in effect deps.
  const setError = useMemo(
    () => (message: string) => {
      setErrorState(message)
      refused(message)
    },
    [],
  )

  const run = async (fn: () => Promise<unknown>, said: string) => {
    setBusy(true)
    setErrorState('')
    setNotice('')
    try {
      await fn()
      window.dispatchEvent(new CustomEvent(SETUP_CHANGED))
      await onDone()
      setNotice(said)
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  return { busy, error, notice, run, setError, setNotice }
}

/**
 * What the last call said: a refusal on the page, where it stays until the next
 * try, and a success as a toast that goes by itself.
 */
export function Messages({ error, notice }: { error: string; notice: string }) {
  return (
    <>
      {error && <Notice className="admin-message">{error}</Notice>}
      <Toast text={notice} />
    </>
  )
}

/** A short confirmation that goes by itself: a HeroUI toast, fired when `text` changes. */
export function Toast({ text }: { text: string }) {
  const last = useRef('')
  useEffect(() => {
    if (text && text !== last.current) showToast(text)
    last.current = text
  }, [text])
  return null
}

/**
 * The timezone field, everywhere it appears.
 *
 * One component rather than three call sites reaching for the same Select, because
 * the three places a timezone is set — an organisation's default, a new branch, an
 * existing branch — must offer the same list. They were three text inputs, and a
 * text input is how a branch ends up with "asia/baku": accepted, unloadable, and
 * silently falling back to UTC for every business day it ever dates.
 */
export function TimezonePicker({
  value,
  onChange,
}: {
  value: string
  onChange: (next: string) => void
}) {
  // Recomputed when the stored value changes, so a zone the browser does not know
  // is carried into the list rather than dropped out of it.
  const options = useMemo(() => timezoneOptions(value), [value])
  const { say } = useStaffText()
  return <SetupPick label={say('admin.branches.timezone')} value={value} onChange={onChange} options={options} placeholder={say('admin.chooseTimezone')} />
}
